import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Pause, Play, Plus, Trash2 } from "lucide-react";
import config from "../../../config";
import { PrimaryButton } from "../../components/ui";
import { ConfirmDialog, EmptyRow, SearchBox, UndoToast } from "../../components/crm/ui";
import { ConversationMark } from "../../components/OlliLogo";
import { InlineLoader, PageLoader } from "../../components/PageLoader";
import { AccessNotice, fetchApi, reasonOf, useMarketingAccess } from "../../components/marketing/access";
import { fmtShort, inr, NO_DATA } from "../../lib/format";
import { adAccountObject, apiGet, campaignTotals, dailyBudgetOf, metaHeaders, objectiveOf, statusOf } from "../../lib/ads";

/**
 * Marketing → Ad campaigns (design: screens/marketing/AdCampaigns.tsx).
 *
 * Real data: the clinic's campaigns (/api/ads/campaigns), their status and
 * daily budget, pause / resume (PATCH /api/ads/campaigns/:id), delete
 * (DELETE /api/ads/campaigns/:id), the page and ad account
 * (useMarketingAccess: /api/integrations, /api/integrations/ads/ads-status;
 * the account's name from /api/ads/accounts) and which campaigns Olli
 * optimises (/api/ads/campaigns/:id/ai-optimization).
 * Spent so far, results and cost per result: lifetime Meta insights for the
 * ad account and each campaign (/api/ads/insights/:objectId, preset=maximum),
 * asked for only once meta_ad_insights (Meta review) is on; until then the
 * numbers say "Coming soon".
 */

const TABS = ["All", "Active", "Paused", "In review"];
const STATUS_STYLE = {
  Active: "bg-success-soft text-success",
  Paused: "bg-mist text-muted",
  Draft: "bg-mist text-ink",
  "In review": "bg-warning-soft text-warning",
};
const DOT = { Active: "bg-success", Paused: "bg-muted", Draft: "bg-ink", "In review": "bg-warning" };
const COLS = "grid-cols-[minmax(170px,2fr)_110px_104px_92px_92px_92px_92px_190px]";

const toCampaign = (row) => ({
  id: row.external_campaign_id,
  name: row.name,
  objective: objectiveOf(row.objective),
  status: statusOf(row),
  dailyBudget: dailyBudgetOf(row),
  createdAt: new Date(row.created_at || row.start_time || Date.now()).getTime(),
  spend: null,
  results: null,
  costPerResult: null,
});

export default function AdCampaigns() {
  const navigate = useNavigate();
  const location = useLocation();
  const access = useMarketingAccess();
  const insightsOn = access.isEnabled("meta_ad_insights");
  const [loading, setLoading] = useState(true);
  const [listReason, setListReason] = useState(null); // why the list didn't load
  const [campaigns, setCampaigns] = useState([]);
  const [optimised, setOptimised] = useState(new Set());
  const [accountName, setAccountName] = useState("");
  const [spentSoFar, setSpentSoFar] = useState(null);
  // Numbers that arrive after the list: show a loader until each answer is in.
  const [pending, setPending] = useState({ spent: true, totals: true, olli: true });
  const done = (part) => setPending((p) => ({ ...p, [part]: false }));
  const [tab, setTab] = useState("All");
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState(() => { const t = location.state?.toast; return t ? { message: t } : null; });
  const [deleting, setDeleting] = useState(null); // the campaign whose delete is being confirmed
  const [busy, setBusy] = useState(false);
  const closeToast = useCallback(() => setToast(null), []);
  const [attempt, setAttempt] = useState(0);
  const accountId = access.adAccount?.id || "";

  useEffect(() => {
    if (access.loading) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      setListReason(null);
      setPending({ spent: true, totals: true, olli: true });
      const [list, accounts] = await Promise.all([
        fetchApi("/api/ads/campaigns", { headers: metaHeaders().headers }),
        localStorage.getItem("meta_user_token_temp") ? apiGet("/api/ads/accounts") : null,
      ]);
      if (!alive) return;
      if (!list.ok) { setListReason(reasonOf(list)); setCampaigns([]); setLoading(false); return; }
      const rows = (list.data?.data?.cached || []).filter((r) => r.external_campaign_id).map(toCampaign);
      setCampaigns(rows);
      const named = (accounts?.data || []).find((a) => a.id === accountId || a.account_id === String(accountId).replace(/^act_/, ""));
      setAccountName(named?.name || "");
      setLoading(false);
      // Lifetime numbers: the ad account's spend, then each campaign's.
      if (accountId && insightsOn) apiGet(`/api/ads/insights/${adAccountObject(accountId)}?preset=maximum`).then((r) => { const v = r?.success ? r.data?.summary?.spend : null; if (!alive) return; if (v != null) setSpentSoFar(Number(v)); done("spent"); });
      else done("spent");
      if (insightsOn) {
        Promise.all(rows.map((c) => campaignTotals(c.id, c.objective, "maximum").then((t) => [c.id, t]))).then((pairs) => {
          if (!alive) return;
          const byId = Object.fromEntries(pairs);
          setCampaigns((current) => current.map((c) => (byId[c.id] ? { ...c, spend: byId[c.id].spend, results: byId[c.id].results, costPerResult: byId[c.id].costPerResult } : c)));
          done("totals");
        }, () => { if (alive) done("totals"); });
      } else done("totals");
      // Which campaigns Olli watches (one small call each).
      const settings = await Promise.all(rows.map((c) => apiGet(`/api/ads/campaigns/${c.id}/ai-optimization`).then((r) => [c.id, !!r?.data?.settings?.enabled])));
      if (alive) { setOptimised(new Set(settings.filter(([, on]) => on).map(([id]) => id))); done("olli"); }
    })();
    return () => { alive = false; };
  }, [access.loading, accountId, insightsOn, attempt]);

  const rows = useMemo(() => campaigns.filter((c) => (tab === "All" || c.status === tab) && c.name.toLowerCase().includes(query.trim().toLowerCase())), [campaigns, tab, query]);
  const active = campaigns.filter((c) => c.status === "Active");
  const daily = active.reduce((s, c) => s + (c.dailyBudget || 0), 0);
  const optimising = campaigns.filter((c) => optimised.has(c.id) && c.status === "Active").length;
  // Not connected: a campaign needs the clinic's page and an ad account.
  const connectReason = access.loading ? null
    : !access.page ? { kind: "connect", title: "Connect your Facebook page", body: "Ads run from the clinic's Facebook page. Connect it (and its Instagram account) to create campaigns." }
    : !access.adAccount ? { kind: "connect", title: "Connect an ad account", body: "Campaigns are paid from a Meta ad account. Connect one in Settings → Integrations to create and launch them." }
    : null;
  const soon = <span className="whitespace-nowrap text-caption text-muted">Coming soon</span>;

  const setStatus = async (c, next) => {
    try {
      await axios.patch(`${config.API_BASE_URL}/api/ads/campaigns/${c.id}`, { status: next === "Active" ? "ACTIVE" : "PAUSED" }, metaHeaders());
      setCampaigns((list) => list.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
      return true;
    } catch (e) {
      setToast({ message: e.response?.data?.message || "Couldn't change the campaign. Open the campaign wizard once to refresh your Meta session." });
      return false;
    }
  };
  const toggle = async (c) => {
    const next = c.status === "Active" ? "Paused" : "Active";
    if (await setStatus(c, next)) setToast({ message: `${c.name} ${next === "Active" ? "resumed" : "paused"}`, onUndo: () => setStatus({ ...c, status: next }, c.status) });
  };
  // Deleted on Meta when the token allows, and from Olli either way (adsController.deleteCampaign).
  const remove = async () => {
    const c = deleting;
    setBusy(true);
    const r = await fetchApi(`/api/ads/campaigns/${c.id}`, { method: "delete", headers: metaHeaders().headers });
    setBusy(false);
    setDeleting(null);
    if (!r.ok) { setToast({ message: r.message || `Couldn't delete ${c.name}. Try again.` }); return; }
    setCampaigns((list) => list.filter((x) => x.id !== c.id));
    setToast({ message: r.data?.meta_deleted ? `${c.name} deleted` : `${c.name} removed from Olli. Delete it in Meta Ads Manager too if it is still there.` });
  };

  return (
    <div className="mx-auto flex max-w-7xl animate-fade-in flex-col gap-6 px-4 py-9 sm:px-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-small font-medium text-muted">Marketing</span>
          <h1 className="text-h1 font-semibold">Ad campaigns</h1>
          <p className="break-words text-small text-muted">{accountId ? `Meta ad account ${[accountName, accountId].filter(Boolean).join(" · ")}` : access.loading ? "" : "No Meta ad account connected"}</p>
        </div>
        <PrimaryButton onClick={() => navigate("/ads/campaigns/new")}><Plus size={18} strokeWidth={1.75} />New campaign</PrimaryButton>
      </header>

      {connectReason && <AccessNotice compact reason={connectReason} />}

      {loading ? <PageLoader label="Loading campaigns…" /> : listReason ? <AccessNotice reason={listReason} onRetry={() => setAttempt((a) => a + 1)} /> : <>
        <section aria-label="Running now" className="flex flex-wrap items-center gap-x-10 gap-y-4 rounded-lg bg-paper px-6 py-5 shadow-soft">
          <div>
            <p className="text-caption text-muted">Running now</p>
            <p className="text-h2 font-semibold tabular-nums">{active.length} <span className="text-body font-normal text-muted">of {campaigns.length} campaigns</span></p>
          </div>
          <div>
            <p className="text-caption text-muted">Daily budget</p>
            <p className="text-h2 font-semibold tabular-nums">{inr(daily)}</p>
          </div>
          <div>
            <p className="text-caption text-muted">Spent so far</p>
            <p className="text-h2 font-semibold tabular-nums">{!insightsOn ? <span className="text-body font-normal text-muted">Coming soon</span> : pending.spent ? <InlineLoader size={24} /> : spentSoFar === null ? NO_DATA : inr(spentSoFar)}</p>
          </div>
          <div className="flex max-w-sm items-start gap-3 lg:ml-auto">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink"><ConversationMark tone="black" className="h-7 w-7" /></span>
            <p className="text-small text-muted"><span className="font-semibold text-ink">Olli is watching {pending.olli ? <InlineLoader size={14} /> : optimising} of them.</span> It checks every morning and asks before it changes anything.</p>
          </div>
        </section>

        {!insightsOn && campaigns.length > 0 && <AccessNotice compact reason={{ kind: "soon", feature: "meta_ad_insights" }} />}

        <section className="overflow-hidden rounded-lg bg-paper shadow-soft">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
            <div role="tablist" aria-label="Filter by status" className="flex flex-wrap gap-1">
              {TABS.map((t) => {
                const n = t === "All" ? campaigns.length : campaigns.filter((c) => c.status === t).length;
                return <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-small font-semibold ${tab === t ? "bg-ink text-paper" : "text-muted hover:bg-mist hover:text-ink"}`}>{t}<span className={tab === t ? "text-paper/65" : "text-muted"}>{n}</span></button>;
              })}
            </div>
            <SearchBox value={query} onChange={setQuery} placeholder="Search campaigns" />
          </div>

          {rows.length ? (
            <div className="overflow-x-auto">
              <div role="table" aria-label="Campaigns" className="min-w-[1060px]">
                <div role="row" className={`grid ${COLS} gap-3 border-b border-line px-5 py-3 text-caption text-muted`}>
                  <span>Name</span><span>Goal</span><span>Status</span><span className="text-right">Daily budget</span><span className="text-right">Spent</span><span className="text-right">Results</span><span className="text-right">Cost each</span><span className="sr-only">Actions</span>
                </div>
                {rows.map((c, i) => (
                  <div role="row" tabIndex={0} key={c.id} onClick={() => navigate(`/ads/campaigns/${c.id}`)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); navigate(`/ads/campaigns/${c.id}`); } }} className={`grid animate-fade-in cursor-pointer ${COLS} items-center gap-3 border-b border-line px-5 py-3.5 text-small outline-none last:border-b-0 hover:bg-mist/50 focus-visible:bg-mist`} style={{ animationDelay: `${Math.min(i, 8) * 25}ms` }}>
                    <span className="flex min-w-0 flex-col">
                      <span className="flex min-w-0 items-center gap-2"><span className="truncate font-semibold">{c.name}</span>{optimised.has(c.id) && <span className="shrink-0 rounded-full bg-sky-soft px-2 py-0.5 text-caption font-semibold">Olli optimising</span>}</span>
                      <span className="text-caption text-muted">Created {fmtShort(c.createdAt)}</span>
                    </span>
                    <span>{c.objective}</span>
                    <span><span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-caption font-semibold ${STATUS_STYLE[c.status]}`}><span className={`h-1.5 w-1.5 rounded-full ${DOT[c.status]}`} />{c.status}</span></span>
                    <span className="text-right tabular-nums">{c.dailyBudget === null ? NO_DATA : inr(c.dailyBudget)}</span>
                    <span className="text-right tabular-nums">{!insightsOn ? soon : pending.totals ? <InlineLoader size={14} /> : c.spend === null ? NO_DATA : inr(c.spend)}</span>
                    <span className="text-right tabular-nums">{!insightsOn ? soon : pending.totals ? <InlineLoader size={14} /> : c.results === null ? NO_DATA : c.results.toLocaleString("en-IN")}</span>
                    <span className="text-right tabular-nums">{!insightsOn ? soon : pending.totals ? <InlineLoader size={14} /> : c.costPerResult === null ? NO_DATA : inr(c.costPerResult)}</span>
                    <span className="flex justify-end">
                      <span className="flex items-center justify-end gap-1.5">
                        <button type="button" onClick={(event) => { event.stopPropagation(); navigate(`/ads/campaigns/${c.id}`); }} onKeyDown={(event) => event.stopPropagation()} className="flex h-8 items-center rounded-full px-2.5 text-caption font-semibold text-sky-deep hover:bg-sky-soft">View</button>
                        {(c.status === "Active" || c.status === "Paused") && (
                          <button type="button" onClick={(event) => { event.stopPropagation(); toggle(c); }} onKeyDown={(event) => event.stopPropagation()} className="flex h-8 items-center gap-1.5 rounded-full px-3 text-caption font-semibold ring-1 ring-line hover:bg-mist">
                            {c.status === "Active" ? <><Pause size={13} strokeWidth={1.75} />Pause</> : <><Play size={13} strokeWidth={1.75} />Resume</>}
                          </button>
                        )}
                        <button type="button" aria-label={`Delete ${c.name}`} onClick={(event) => { event.stopPropagation(); setDeleting(c); }} onKeyDown={(event) => event.stopPropagation()} className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-danger-soft hover:text-danger"><Trash2 size={14} strokeWidth={1.75} /></button>
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyRow
              title={campaigns.length ? "No campaigns match" : "No campaigns yet"}
              body={campaigns.length ? "Try another search or status." : "Describe what you want to promote and Olli sets up the whole campaign."}
              action={campaigns.length ? <button onClick={() => { setTab("All"); setQuery(""); }} className="flex h-10 items-center rounded-full px-5 text-small font-semibold ring-1 ring-line hover:bg-mist">Clear filters</button> : undefined}
            />
          )}
        </section>
      </>}
      {deleting && <ConfirmDialog title={`Delete ${deleting.name}?`} body="This deletes the campaign on Meta, with its ad sets and ads, and removes it from Olli. It can't be undone." confirmLabel={busy ? "Deleting…" : "Delete campaign"} tone="danger" onCancel={() => setDeleting(null)} onConfirm={() => { if (!busy) remove(); }} />}
      {toast && <UndoToast message={toast.message} onUndo={toast.onUndo} onDone={closeToast} />}
    </div>
  );
}
